import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, interval, Observable, Subject } from 'rxjs';
import { Location, PopStateEvent, NgIf, AsyncPipe } from '@angular/common';
import { Event as RouterEvent, NavigationEnd, NavigationStart, Router, RouterOutlet, RouterLink } from '@angular/router';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { SettingsService } from './services/settings.service';
import { CompanySettings } from './models/settings';
import { MatTooltip } from '@angular/material/tooltip';
import { NavbarComponent } from './components/navbar/navbar.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { VersionService } from './services/version.service';

@Component({
    selector: 'app-root',

    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        SidebarComponent,
        NavbarComponent,
        RouterOutlet,
        NgIf,
        MatTooltip,
        RouterLink,
        AsyncPipe,
    ],
})
export class AppComponent implements OnInit, OnDestroy {
  public frontendVersion;
  public companySettings$: Observable<CompanySettings>;
  public showVersionUpgradeHint = false;
  public showReloadButton = false;
  private hasPendingVersionInfo = false;
  private readonly acknowledgedBackendVersionKey = 'currentBackendVersion';
  private readonly acknowledgedFrontendVersionKey = 'currentFrontendVersion';
  private readonly legacyAcknowledgedVersionKey = 'currentVersion';
  private acknowledgedBackendVersion?: string;
  private acknowledgedFrontendVersion?: string;
  private latestBackendVersion?: string;
  private latestFrontendVersion?: string;
  private readonly versionCheckIntervalMs = 5 * 60 * 1000;
  private readonly reloadAcknowledgeKey = 'acknowledgeVersionOnReload';
  private _lastPoppedUrl?: string;
  private _yScrollStack: number[] = [];

  private _subject = new Subject<void>();

  constructor(
    public location: Location,
    private router: Router,
    private settingsService: SettingsService,
    private readonly versionService: VersionService
  ) {
    this.frontendVersion = this.versionService.frontendVersion;
    this.companySettings$ = this.settingsService.get();
    const legacyAcknowledgedVersion =
      localStorage.getItem(this.legacyAcknowledgedVersionKey) || undefined;
    this.acknowledgedBackendVersion =
      localStorage.getItem(this.acknowledgedBackendVersionKey) ||
      legacyAcknowledgedVersion;
    this.acknowledgedFrontendVersion =
      localStorage.getItem(this.acknowledgedFrontendVersionKey) ||
      legacyAcknowledgedVersion;
  }
  ngOnInit() {
    this.startVersionMonitoring();

    this.location.subscribe((ev: PopStateEvent) => {
      this._lastPoppedUrl = ev.url;
    });
    this.router.events
      .pipe(takeUntil(this._subject))
      .subscribe((event: RouterEvent) => {
        if (event instanceof NavigationStart) {
          if (event.url !== this._lastPoppedUrl) {
            this._yScrollStack.push(window.scrollY);
          }
        } else if (event instanceof NavigationEnd) {
          if (event.url === this._lastPoppedUrl) {
            this._lastPoppedUrl = undefined;
            window.scrollTo(0, this._yScrollStack.pop() || 0);
          } else {
            window.scrollTo(0, 0);
          }
        }
      });

    this.router.events
      .pipe(takeUntil(this._subject))
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const elemMainPanel = document.querySelector<HTMLElement>('.main-panel');
        const elemSidebar = document.querySelector<HTMLElement>(
          '.sidebar .sidebar-wrapper'
        );

        if (elemMainPanel) {
          elemMainPanel.scrollTop = 0;
        }
        if (elemSidebar) {
          elemSidebar.scrollTop = 0;
        }
      });
  }

  closeVersionUpgradeHint() {
    this.showVersionUpgradeHint = false;
    this.showReloadButton = false;
    this.hasPendingVersionInfo = false;
    const backendVersionToRemember =
      this.latestBackendVersion || this.acknowledgedBackendVersion;
    if (backendVersionToRemember) {
      localStorage.setItem(
        this.acknowledgedBackendVersionKey,
        backendVersionToRemember
      );
      this.acknowledgedBackendVersion = backendVersionToRemember;
    }

    const frontendVersionToRemember =
      this.latestFrontendVersion || this.frontendVersion;
    localStorage.setItem(
      this.acknowledgedFrontendVersionKey,
      frontendVersionToRemember
    );
    this.acknowledgedFrontendVersion = frontendVersionToRemember;
  }

  reloadApp() {
    sessionStorage.setItem(this.reloadAcknowledgeKey, '1');
    const backendVersionToRemember =
      this.latestBackendVersion || this.acknowledgedBackendVersion;
    if (backendVersionToRemember) {
      localStorage.setItem(
        this.acknowledgedBackendVersionKey,
        backendVersionToRemember
      );
      this.acknowledgedBackendVersion = backendVersionToRemember;
    }

    const frontendVersionToRemember =
      this.latestFrontendVersion || this.frontendVersion;
    localStorage.setItem(
      this.acknowledgedFrontendVersionKey,
      frontendVersionToRemember
    );
    this.acknowledgedFrontendVersion = frontendVersionToRemember;

    window.location.reload();
  }

  get changelogLink(): string {
    const version = this.latestFrontendVersion || this.frontendVersion;
    return `https://docs.gp2.app/changelog#${version}`;
  }

  ngOnDestroy(): void {
    this._subject.next();
    this._subject.complete();
  }

  private startVersionMonitoring() {
    this.versionService
      .getBackendVersion()
      .pipe(takeUntil(this._subject))
      .subscribe((version) => this.handleBackendVersionResponse(version));
    this.versionService
      .getFrontendVersion()
      .pipe(takeUntil(this._subject))
      .subscribe((version) => this.handleFrontendVersionResponse(version));

    interval(this.versionCheckIntervalMs)
      .pipe(
        takeUntil(this._subject),
        switchMap(() =>
          forkJoin({
            backend: this.versionService.getBackendVersion(),
            frontend: this.versionService.getFrontendVersion(),
          })
        )
      )
      .subscribe(({ backend, frontend }) => {
        this.handleBackendVersionResponse(backend);
        this.handleFrontendVersionResponse(frontend);
      });
  }

  private handleBackendVersionResponse(version?: { backendVersion?: string }) {
    if (!version) {
      return;
    }

    this.latestBackendVersion = version.backendVersion;

    if (version.backendVersion) {
      if (!this.acknowledgedBackendVersion) {
        this.acknowledgedBackendVersion = version.backendVersion;
        localStorage.setItem(
          this.acknowledgedBackendVersionKey,
          version.backendVersion
        );
      }
    }
    this.updateUpgradeHint();
  }

  private handleFrontendVersionResponse(version?: {
    frontendVersion?: string;
    frontendVersionSha?: string;
  }) {
    if (!version) {
      return;
    }

    this.latestFrontendVersion =
      version.frontendVersionSha || version.frontendVersion;

    if (this.latestFrontendVersion) {
      if (!this.acknowledgedFrontendVersion) {
        this.acknowledgedFrontendVersion = this.latestFrontendVersion;
        localStorage.setItem(
          this.acknowledgedFrontendVersionKey,
          this.latestFrontendVersion
        );
      }
    }

    this.updateUpgradeHint();
  }

  private updateUpgradeHint() {
    if (this.maybeAcknowledgeAfterReload()) {
      return;
    }

    const backendMismatch =
      !!this.latestBackendVersion &&
      !!this.acknowledgedBackendVersion &&
      this.latestBackendVersion !== this.acknowledgedBackendVersion;
    const frontendMismatch =
      !!this.latestFrontendVersion &&
      !!this.acknowledgedFrontendVersion &&
      this.latestFrontendVersion !== this.acknowledgedFrontendVersion;

    this.showReloadButton = backendMismatch || frontendMismatch;
    if (this.showReloadButton) {
      this.hasPendingVersionInfo = true;
    }
    this.showVersionUpgradeHint = this.hasPendingVersionInfo;
  }

  private maybeAcknowledgeAfterReload(): boolean {
    if (sessionStorage.getItem(this.reloadAcknowledgeKey) !== '1') {
      return false;
    }

    let updated = false;
    if (this.latestBackendVersion) {
      localStorage.setItem(
        this.acknowledgedBackendVersionKey,
        this.latestBackendVersion
      );
      this.acknowledgedBackendVersion = this.latestBackendVersion;
      updated = true;
    }

    if (this.latestFrontendVersion) {
      localStorage.setItem(
        this.acknowledgedFrontendVersionKey,
        this.latestFrontendVersion
      );
      this.acknowledgedFrontendVersion = this.latestFrontendVersion;
      updated = true;
    }

    if (updated) {
      sessionStorage.removeItem(this.reloadAcknowledgeKey);
      this.hasPendingVersionInfo = true;
      this.showVersionUpgradeHint = true;
      this.showReloadButton = false;
      return true;
    }

    return false;
  }
}
